import { useState } from 'react';
import { AccountData, ReportPresetData } from '../api';
import { useAuth } from '../session/InternalAuthProvider';
import { useLanguage } from '../common/GeneralUtils';
import { useReportColumns } from './swr/useReportColumns';
import { ALL_COLUMNS_PRESET_ID, applyPresetToCsv } from './reportPresets';

/**
 * Column-set state for a report screen: the report's columns, the account's saved presets, the
 * current selection, and the filter to run over the CSV the server returns.
 *
 * Presets are held locally after a save, from the account the PUT returns. The same account also
 * replaces the cached authenticated account, so the other report screen sees the saved presets.
 */
export const useReportPresetSelection = (reportKind: 'orders' | 'workshops') => {
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const language = useLanguage();
  const { columns } = useReportColumns(language);
  const [presets, setPresets] = useState<ReportPresetData[]>(account.report_presets ?? []);
  const storageKey = `webook_reportPreset_${reportKind}_${account.id}`;
  const [selectedPresetId, setSelectedPresetId] = useState(() => {
    try {
      const storedPresetId = localStorage.getItem(storageKey);
      const isStoredPresetKnown = (account.report_presets ?? []).some(
        (preset) => (preset.id ?? preset.label) === storedPresetId,
      );
      return storedPresetId && isStoredPresetKnown ? storedPresetId : ALL_COLUMNS_PRESET_ID;
    } catch {
      return ALL_COLUMNS_PRESET_ID;
    }
  });

  const selectPreset = (presetId: string) => {
    setSelectedPresetId(presetId);
    try {
      localStorage.setItem(storageKey, presetId);
    } catch {
      // storage unavailable: the selection is just not remembered
    }
  };

  const selectedPreset = presets.find((preset) => (preset.id ?? preset.label) === selectedPresetId);

  const filterCsv = (csv: string): string =>
    selectedPreset ? applyPresetToCsv(csv, columns, selectedPreset.field_keys ?? []) : csv;

  const orderPageIds = selectedPreset?.order_page_ids?.length ? selectedPreset.order_page_ids : undefined;

  return {
    columns,
    presets,
    setPresets,
    selectedPresetId,
    setSelectedPresetId: selectPreset,
    filterCsv,
    orderPageIds,
  };
};
